import React, { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { stepsComponents } from '../../survey/stepsComponents';
import useViewportUnits from '../../hooks/useViewportUnits';
import { useAppStore } from '../../store';
import s from './index.module.scss';

type Step = {
  name: string;
  [key: string]: unknown;
};

type StepsProps = {
  className: string;
  prev: Step | null;
  current: Step | null;
  next: Step | null;
};

const renderers: Record<string, ((step: Step) => React.ReactElement) | undefined> = stepsComponents;

const Steps = React.memo(
  ({
    className, prev, current, next,
  }: StepsProps) => (
    <div className={className}>
      {prev && renderers[prev.name] && (
        <div className={`${s.stepWrapper} ${s.previousStepWrapper}`} key={`prev:${prev.name}`}>
          {renderers[prev.name]!(prev)}
        </div>
      )}

      {current && renderers[current.name] && (
        <div
          id="stepWrapperId"
          className={`${s.stepWrapper} ${s.currentStepWrapper}`}
          key={`current:${current.name}`}
        >
          {renderers[current.name]!(current)}
        </div>
      )}

      {next && renderers[next.name] && (
        <div className={`${s.stepWrapper} ${s.nextStepWrapper}`} key={`next:${next.name}`}>
          {renderers[next.name]!(next)}
        </div>
      )}
    </div>
  ),
  (prevProps, nextProps) => {
    const sameClass = prevProps.className === nextProps.className;

    const prevNamePrev = prevProps.prev?.name ?? null;
    const prevNameNext = nextProps.prev?.name ?? null;

    const currNamePrev = prevProps.current?.name ?? null;
    const currNameNext = nextProps.current?.name ?? null;

    const nextNamePrev = prevProps.next?.name ?? null;
    const nextNameNext = nextProps.next?.name ?? null;

    return (
      sameClass
      && prevNamePrev === prevNameNext
      && currNamePrev === currNameNext
      && nextNamePrev === nextNameNext
    );
  },
);

function StepsContent() {
  const {
    stepsList, currentStepIndex, previousStep, nextStep,
  } = useAppStore(
    useShallow((state) => ({
      stepsList: (state.list as Step[] | null | undefined) || null,
      previousStep: state.previousStep as unknown as Step | null | undefined,
      nextStep: state.nextStep as unknown as Step | null | undefined,
      currentStepIndex: state.currentStepIndex as number | undefined,
    })),
  );

  const list: Step[] = stepsList || [];
  const safeIndex = Number.isInteger(currentStepIndex) ? (currentStepIndex as number) : 0;
  const isFirstStep = safeIndex === 0;

  // На первом шаге второй НЕ рендерим, пока страница не загрузилась полностью
  // (window 'load'): иначе ассеты второго шага конкурируют за сеть/CPU в окне
  // рендера LCP. После load в idle включаем фоновую предзагрузку — тогда переход
  // 1→2 проходит без видимой подгрузки lazy-чанка.
  const [preloadNextOnFirst, setPreloadNextOnFirst] = useState(false);
  useEffect(() => {
    if (!isFirstStep || preloadNextOnFirst) return undefined;

    const w = window as typeof window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const schedulePreload = (): void => {
      if (typeof w.requestIdleCallback === 'function') {
        idleId = w.requestIdleCallback(() => setPreloadNextOnFirst(true));
      } else {
        timeoutId = setTimeout(() => setPreloadNextOnFirst(true), 300);
      }
    };

    if (document.readyState === 'complete') {
      schedulePreload();
    } else {
      window.addEventListener('load', schedulePreload, { once: true });
    }

    return () => {
      window.removeEventListener('load', schedulePreload);
      if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [isFirstStep, preloadNextOnFirst]);

  useEffect(() => {
    const stepWrapperCurrent = document.querySelector<HTMLElement>('#stepWrapperId');
    useViewportUnits(stepWrapperCurrent, { extraBottomPx: 182 });
  }, [currentStepIndex]);

  const prevIdx = useMemo<number | null>(
    () => (previousStep ? safeIndex - 1 : null),
    [previousStep, safeIndex],
  );
  const nextIdx = useMemo<number | null>(
    () => (nextStep ? safeIndex + 1 : null),
    [nextStep, safeIndex],
  );

  const { prev, current, next } = useMemo(() => {
    const safe = (idx: number | null): Step | null => (
      idx !== null && idx >= 0 && idx < list.length ? list[idx] : null
    );
    // На первом шаге next не рендерим до window 'load', затем в idle включаем
    // фоновую предзагрузку: рендерим скрытый next-wrapper, что подтягивает чанк шага 2.
    const skipNext = isFirstStep && !preloadNextOnFirst;
    return {
      prev: safe(prevIdx),
      current: list[safeIndex] || null,
      next: skipNext ? null : safe(nextIdx),
    };
  }, [list, safeIndex, prevIdx, nextIdx, isFirstStep, preloadNextOnFirst]);

  if (!current) return null;

  return (
    <Steps
      className={s.stepsContent}
      prev={prev}
      current={current}
      next={next}
    />
  );
}

export default StepsContent;
